"use client";

import { resolveApiAssetUrl } from "@/lib/backend";
import type { ClientLogo } from "@/lib/types";
import Image from "next/image";

type Props = {
  item: ClientLogo;
  displayName: string;
};

export function ClientLogoCard({ item, displayName }: Props) {
  const logoSrc = resolveApiAssetUrl(item.logoUrl);
  const website = item.website?.trim();

  const inner = (
    <>
      <div className="relative flex h-24 w-full items-center justify-center p-4 md:h-28">
        {logoSrc ? (
          <Image
            src={logoSrc}
            alt={item.logoAlt || displayName}
            width={200}
            height={80}
            className="max-h-full max-w-full object-contain transition duration-300 motion-safe:group-hover:scale-[1.03]"
            sizes="(max-width: 768px) 50vw, 200px"
          />
        ) : null}
      </div>
      {displayName ? (
        <p className="border-t border-[var(--border)] px-4 py-3 text-center text-sm font-semibold text-[var(--foreground)]">
          {displayName}
        </p>
      ) : null}
    </>
  );

  const cardClass =
    "group flex flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)] shadow-sm transition duration-300 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md";

  if (website) {
    return (
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cardClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2`}
      >
        {inner}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    );
  }

  return <article className={cardClass}>{inner}</article>;
}
